import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { meApi } from "@/lib/api/me";
import { askKeys } from "@/hooks/queries/use-ask-products";
import type { StoreFogbugzKeyInput, SetAdoPatInput, SetPreferredHostInput } from "@/lib/api/me";

export const meKeys = {
  all: ["me"] as const,
  me: () => [...meKeys.all, "current"] as const,
  adoPat: () => [...meKeys.all, "ado-pat"] as const,
};

/**
 * Hook to get current user information including FogBugz key status
 */
export function useMe() {
  return useQuery({
    queryKey: meKeys.me(),
    queryFn: () => meApi.me(),
    select: (response) => response.data,
  });
}

/**
 * Hook to store a FogBugz API key
 */
export function useStoreFogbugzKey() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: StoreFogbugzKeyInput) => meApi.storeFogbugzKey(data),
    onSuccess: () => {
      // Invalidate the /me query to refresh hasFogbugzKey status
      void queryClient.invalidateQueries({ queryKey: meKeys.me() });
      // Product Q&A lists FogBugz products only with the person's own key
      void queryClient.invalidateQueries({ queryKey: askKeys.products() });
    },
  });
}

/**
 * Hook to set the user's preferred CLI host (A14 dual-host)
 */
export function useUpdatePreferredHost() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SetPreferredHostInput) => meApi.setPreferredHost(data),
    onSuccess: () => {
      // Invalidate the /me query to refresh preferredHost
      void queryClient.invalidateQueries({ queryKey: meKeys.me() });
    },
  });
}

/**
 * Hook to clear the FogBugz API key
 */
export function useClearFogbugzKey() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => meApi.clearFogbugzKey(),
    onSuccess: () => {
      // Invalidate the /me query to refresh hasFogbugzKey status
      void queryClient.invalidateQueries({ queryKey: meKeys.me() });
      // Product Q&A lists FogBugz products only with the person's own key
      void queryClient.invalidateQueries({ queryKey: askKeys.products() });
    },
  });
}

/**
 * Hook to store the user's Azure DevOps PAT
 */
export function useSetAdoPat() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SetAdoPatInput) => meApi.setAdoPat(data),
    onSuccess: async () => {
      // Invalidate the /me query to refresh hasAdoPat status
      await queryClient.invalidateQueries({ queryKey: meKeys.me() });
      await queryClient.invalidateQueries({ queryKey: meKeys.adoPat() });
    },
  });
}

/**
 * The caller's Azure DevOps PAT status and the how-to values for making one
 * (GET /me/ado-pat; epic PI-SPEC-SW-E288). Never the token.
 */
export function useAdoPatStatus() {
  return useQuery({
    queryKey: meKeys.adoPat(),
    queryFn: () => meApi.getAdoPatStatus(),
    select: (response) => response.data,
  });
}

/**
 * Test an Azure DevOps PAT against ADO and save it only when it works
 * (POST /me/ado-pat/connect). A passed test refreshes the /me and PAT status
 * queries, so PAT-guarded pages open without a reload; a refusal stores
 * nothing and refreshes nothing.
 */
export function useConnectAdoPat() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: SetAdoPatInput) => (await meApi.connectAdoPat(data)).data,
    onSuccess: async (result) => {
      if (!result.ok) return;
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: meKeys.me() }),
        queryClient.invalidateQueries({ queryKey: meKeys.adoPat() }),
      ]);
    },
  });
}

/**
 * Hook to clear the user's Azure DevOps PAT
 */
export function useClearAdoPat() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => meApi.clearAdoPat(),
    onSuccess: async () => {
      // Invalidate the /me query to refresh hasAdoPat status
      await queryClient.invalidateQueries({ queryKey: meKeys.me() });
      await queryClient.invalidateQueries({ queryKey: meKeys.adoPat() });
    },
  });
}
